import React, { useCallback, useState } from "react";
import { useTranslation } from 'react-i18next';
import { toast } from "@/components/ui/use-toast";
import styles from "@/styles/components/LogoCropModal.module.css";
import Cropper from "react-easy-crop";

function getCroppedImg(imageSrc, areaPixels) {
  return new Promise(async (resolve, reject) => {
    try {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = imageSrc;
      await new Promise((r, e) => {
        image.onload = r;
        image.onerror = e;
      });

      const size = 512; // render a high-res square, backend resizes to 256
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no ctx"));

      // Draw the selected crop area scaled to the canvas
      const { x, y, width, height } = areaPixels; // area in source image pixels
      ctx.clearRect(0, 0, size, size);
      // drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
      // We scale the crop to fill the canvas square
      const dSize = Math.min(size, Math.round((size * width) / Math.max(width, height)));
      ctx.drawImage(image, x, y, width, height, 0, 0, size, size);

      // Mask into a circle
      const circleCanvas = document.createElement("canvas");
      circleCanvas.width = size;
      circleCanvas.height = size;
      const cctx = circleCanvas.getContext("2d");
      if (!cctx) return reject(new Error("no cctx"));
      cctx.clearRect(0, 0, size, size);
      cctx.save();
      cctx.beginPath();
      cctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      cctx.closePath();
      cctx.clip();
      cctx.drawImage(canvas, 0, 0);
      cctx.restore();

      circleCanvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("toBlob failed"));
          resolve(blob);
        },
        "image/png",
        0.95
      );
    } catch (e) {
      reject(e);
    }
  });
}

export default function LogoCropModal({ file, open, onClose, onCropped }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [areaPixels, setAreaPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);

  React.useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
      setPreview(null);
    };
  }, [file]);

  const doCrop = useCallback(async () => {
    if (!preview || !areaPixels) return;
    try {
      setProcessing(true);
      const blob = await getCroppedImg(preview, areaPixels);
      onCropped(blob);
    } catch (e) {
      console.error(e);
      toast({ title: t('common.alerts.imageProcessingFailed'), variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  }, [preview, areaPixels, onCropped]);

  if (!open) return null;

  return (
    <div className={styles.backdrop}>
      <div className="bg-background rounded shadow-lg w-full max-w-lg">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm font-semibold">Logo positionieren</div>
          <button className="text-xs border px-2 py-1 rounded" onClick={onClose}>
            Schließen
          </button>
        </div>
        <div className="p-4">
          {preview ? (
            <div className="relative w-full h-64 bg-muted rounded overflow-hidden">
              <Cropper
                image={preview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setAreaPixels(areaPixels)}
              />
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Lade Bild…</div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <label className="text-xs text-muted-foreground">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
            />
          </div>
        </div>
        <div className="p-3 border-t flex justify-end gap-2">
          <button className="text-xs border px-3 py-1 rounded" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-60"
            disabled={processing}
            onClick={doCrop}
          >
            {processing ? "…" : "Übernehmen"}
          </button>
        </div>
      </div>
    </div>
  );
}
